import React, { useState, useEffect, useRef, useMemo } from 'react';
import axios from 'axios';
import './Documents.css';

const API = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
const SUPABASE_URL = 'https://yulypcgdgxzqtdkysywg.supabase.co';
const SUPABASE_KEY = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Inl1bHlwY2dkZ3h6cXRka3lzeXdnIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzcwMzM4NDcsImV4cCI6MjA5MjYwOTg0N30.jeMCN3jQaIzdZor8I_ZdKLg5pBx6iiPdGQdaV26RrUw';

const SECTION_NAMES = {
  1: 'System Assurance & Compliance',
  2: 'Training & Capability',
  3: 'Working Hours',
  4: 'Workplace Safety',
  5: 'Occupational Health',
  6: 'Site Security',
  7: 'Environmental Management',
  8: 'Subcontractors',
  9: 'Sourcing & Goods',
  10: 'Work Equipment',
};

const SECTION_FULL = (n) => n ? `Section ${n} — ${SECTION_NAMES[n] || 'Other'}` : '—';

const CATEGORY_COLOURS = {
  certificate: '#f5b50b',
  policy: '#4CAF50',
  procedure: '#2196F3',
  'risk assessment': '#FF9800',
  record: '#9C27B0',
  other: '#607D8B',
};

const CATEGORIES = ['All', 'Policy', 'Procedure', 'Certificate', 'Risk Assessment', 'Record'];
const STATUSES = ['All', 'Current', 'Expiring Soon', 'Overdue', 'Archived'];

const EMPTY_FORM = {
  title: '', description: '', doc_ref: '', category: 'policy', section: '',
  version: '1.0', status: 'active', expiry_date: '', review_date: '',
  reminder_days: 30, requires_acknowledgement: false, external_link: '',
};

function getDocStatus(doc, expiringIds) {
  if (doc.is_archived) return 'Archived';
  if (expiringIds.has(doc.id)) {
    const days = doc.expiry_date
      ? Math.ceil((new Date(doc.expiry_date) - new Date()) / 86400000)
      : null;
    return days !== null && days < 0 ? 'Overdue' : 'Expiring Soon';
  }
  return 'Current';
}

function formatDate(str) {
  if (!str) return 'N/A';
  const d = new Date(str);
  return isNaN(d) ? str : d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

function capitalise(s) {
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : '';
}

function StatusBadge({ status }) {
  const cls = { Current: 'badge-green', 'Expiring Soon': 'badge-amber', Overdue: 'badge-red', Archived: 'badge-grey' };
  return <span className={`reg-badge ${cls[status] || 'badge-grey'}`}>{status}</span>;
}

function exportToCSV(docs, expiringIds) {
  const headers = ['Ref', 'Title', 'Category', 'Section', 'Version', 'Issue Date', 'Review / Expiry', 'Status', 'Description'];
  const rows = docs.map(d => [
    d.doc_ref || 'N/A',
    d.title || '',
    capitalise(d.category || ''),
    SECTION_FULL(d.section),
    d.version || '',
    formatDate(d.created_at),
    formatDate(d.expiry_date || d.review_date),
    getDocStatus(d, expiringIds),
    d.description || '',
  ]);
  const BOM = '\uFEFF';
  const csv = BOM + [headers, ...rows]
    .map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(','))
    .join('\r\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `Bramley_Document_Register_${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

export default function Documents({ user }) {
  const [documents, setDocuments] = useState([]);
  const [expiring, setExpiring] = useState([]);
  const [archivedDocs, setArchivedDocs] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  // View state
  const [view, setView] = useState('card'); // 'card' | 'table'
  const [search, setSearch] = useState('');
  const [catFilter, setCatFilter] = useState('All');
  const [statusFilter, setStatusFilter] = useState('All');
  const [showArchived, setShowArchived] = useState(false);
  const [sortCol, setSortCol] = useState('section');
  const [sortDir, setSortDir] = useState('asc');

  // Modal / form state
  const [selected, setSelected] = useState(null);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [analysing, setAnalysing] = useState(false);
  const [analysisStatus, setAnalysisStatus] = useState('');
  const [uploadProgress, setUploadProgress] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const [detectedPerson, setDetectedPerson] = useState(null);

  const fileInputRef = useRef();

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    try {
      const [docsRes, expiringRes, archivedRes, usersRes] = await Promise.all([
        axios.get(`${API}/documents`),
        axios.get(`${API}/documents/expiring`),
        axios.get(`${API}/documents/archived`),
        axios.get(`${API}/users/all`),
      ]);
      setDocuments(docsRes.data);
      setExpiring(expiringRes.data);
      setArchivedDocs(archivedRes.data);
      setAllUsers(usersRes.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const expiringIds = useMemo(() => new Set(expiring.map(e => e.id)), [expiring]);

  const daysUntilExpiry = (date) => Math.ceil((new Date(date) - new Date()) / 86400000);

  // ── Filtering & sorting ──────────────────────────────────────
  const allDocs = useMemo(() => {
    return showArchived ? [...documents, ...archivedDocs] : documents;
  }, [documents, archivedDocs, showArchived]);

  const filtered = useMemo(() => {
    return allDocs
      .filter(d => catFilter === 'All' || d.category === catFilter.toLowerCase())
      .filter(d => {
        if (statusFilter === 'All') return true;
        return getDocStatus(d, expiringIds) === statusFilter;
      })
      .filter(d => {
        if (!search) return true;
        const q = search.toLowerCase();
        return (
          (d.title || '').toLowerCase().includes(q) ||
          (d.doc_ref || '').toLowerCase().includes(q) ||
          (d.description || '').toLowerCase().includes(q) ||
          (d.category || '').toLowerCase().includes(q)
        );
      })
      .sort((a, b) => {
        let av = a[sortCol] ?? '';
        let bv = b[sortCol] ?? '';
        if (typeof av === 'string') av = av.toLowerCase();
        if (typeof bv === 'string') bv = bv.toLowerCase();
        if (av < bv) return sortDir === 'asc' ? -1 : 1;
        if (av > bv) return sortDir === 'asc' ? 1 : -1;
        return 0;
      });
  }, [allDocs, catFilter, statusFilter, search, sortCol, sortDir, expiringIds]);

  const groupedBySection = useMemo(() => {
    return filtered.reduce((acc, doc) => {
      const s = doc.section || 0;
      if (!acc[s]) acc[s] = [];
      acc[s].push(doc);
      return acc;
    }, {});
  }, [filtered]);

  const counts = useMemo(() => ({
    total: documents.length,
    current: documents.filter(d => !expiringIds.has(d.id)).length,
    expiring: expiring.filter(d => daysUntilExpiry(d.expiry_date) >= 0).length,
    overdue: expiring.filter(d => daysUntilExpiry(d.expiry_date) < 0).length,
    archived: archivedDocs.length,
  }), [documents, expiring, archivedDocs, expiringIds]);

  const handleSort = (col) => {
    if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortCol(col); setSortDir('asc'); }
  };

  // ── File analysis ────────────────────────────────────────────
  const toBase64 = (f) => new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(r.result.split(',')[1]);
    r.onerror = rej;
    r.readAsDataURL(f);
  });

  const analyseDocument = async (selectedFile) => {
    setAnalysing(true);
    setAnalysisStatus('Reading document...');
    try {
      if (!selectedFile.type.startsWith('image/') && selectedFile.type !== 'application/pdf') {
        const name = selectedFile.name.replace(/\.[^/.]+$/, '').replace(/[_-]/g, ' ');
        setForm(prev => ({ ...prev, title: name }));
        setAnalysisStatus('Word doc — title extracted. Fill in remaining fields.');
        return;
      }
      setAnalysisStatus('Sending to Claude for analysis...');
      const base64 = await toBase64(selectedFile);
      const response = await axios.post(`${API}/analyse`, { base64, mediaType: selectedFile.type });
      const parsed = response.data;
      setForm(prev => ({
        ...prev,
        title: parsed.title || prev.title,
        description: parsed.description || prev.description,
        doc_ref: parsed.doc_ref || prev.doc_ref,
        category: parsed.category || prev.category,
        section: parsed.section ? String(parsed.section) : prev.section,
        version: parsed.version || prev.version,
        expiry_date: parsed.expiry_date || prev.expiry_date,
        review_date: parsed.review_date || prev.review_date,
        requires_acknowledgement: parsed.requires_acknowledgement ?? prev.requires_acknowledgement,
        reminder_days: parsed.reminder_days || prev.reminder_days,
        _training_type: parsed.training_type || '',
        _provider: parsed.provider || '',
      }));
      if (parsed.person_first_name && parsed.person_last_name) {
        const match = allUsers.find(u =>
          u.first_name.toLowerCase() === parsed.person_first_name.toLowerCase() &&
          u.last_name.toLowerCase() === parsed.person_last_name.toLowerCase()
        );
        if (match) setDetectedPerson(match);
      }
      setAnalysisStatus('Done! Check the fields below and save.');
      setTimeout(() => setAnalysisStatus(''), 3000);
    } catch (err) {
      console.error(err);
      setAnalysisStatus('Large document — please fill in the fields manually.');
      setTimeout(() => setAnalysisStatus(''), 4000);
    } finally {
      setAnalysing(false);
    }
  };

  const handleFileSelect = (f) => {
    if (!f) return;
    setFile(f);
    analyseDocument(f);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files[0];
    if (f) handleFileSelect(f);
  };

  // ── Submit ───────────────────────────────────────────────────
  const handleSubmit = async () => {
    if (!form.title) { alert('Title is required.'); return; }
    setSaving(true);
    let file_url = '';
    try {
      if (file) {
        setUploadProgress('Uploading file...');
        const ext = file.name.split('.').pop();
        const safeName = form.title.replace(/[^a-zA-Z0-9]/g, '_').substring(0, 40);
        let path;
        if (detectedPerson) {
          const folder = `${detectedPerson.first_name.toLowerCase()}-${detectedPerson.last_name.toLowerCase()}`;
          const initials = `${detectedPerson.first_name[0]}${detectedPerson.last_name[0]}`.toUpperCase();
          path = `training-records/${folder}/${initials}_${safeName}_v${form.version}.${ext}`;
        } else {
          path = `documents/${form.category.replace(/\s/g, '-')}/${safeName}_v${form.version}.${ext}`;
        }
        const uploadRes = await fetch(`${SUPABASE_URL}/storage/v1/object/vera-documents/${path}`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${SUPABASE_KEY}`,
            'Content-Type': file.type,
            'x-upsert': 'true',
          },
          body: file,
        });
        if (!uploadRes.ok) throw new Error('File upload failed');
        file_url = `${SUPABASE_URL}/storage/v1/object/public/vera-documents/${path}`;
        setUploadProgress('Saving record...');
      }

      if (detectedPerson && (form.category === 'certificate' || form._training_type) && file_url) {
        try {
          await axios.post(`${API}/training`, {
            user_id: detectedPerson.id,
            training_type: form._training_type || form.title,
            provider: form._provider || '',
            date_completed: null,
            expiry_date: form.expiry_date || null,
            certificate_url: file_url,
            status: 'current',
            reminder_days: parseInt(form.reminder_days) || 90,
            notes: form.description || '',
            created_by: user.id,
          });
        } catch (err) { console.error('Training record error:', err); }
      }

      const { _training_type, _provider, ...cleanForm } = form;
      await axios.post(`${API}/documents`, {
        ...cleanForm,
        section: form.section ? parseInt(form.section) : null,
        reminder_days: parseInt(form.reminder_days) || 30,
        expiry_date: form.expiry_date || null,
        review_date: form.review_date || null,
        external_link: form.external_link || null,
        file_url: file_url || null,
        owner_id: user.id,
        created_by: user.id,
      });

      setAdding(false);
      setForm(EMPTY_FORM);
      setFile(null);
      setUploadProgress('');
      setDetectedPerson(null);
      fetchAll();
    } catch (err) {
      alert('Error saving: ' + err.message);
    } finally {
      setSaving(false);
      setUploadProgress('');
    }
  };

  const handleArchiveDoc = async (docId) => {
    if (!window.confirm('Archive this document?')) return;
    try {
      await axios.patch(`${API}/documents/${docId}/archive`, { archived_by: user.id });
      setSelected(null);
      fetchAll();
    } catch (err) { alert('Error archiving document.'); }
  };

  const handleUnarchiveDoc = async (docId) => {
    try {
      await axios.patch(`${API}/documents/${docId}`, { is_archived: false, archived_at: null, archived_by: null, status: 'active' });
      setSelected(null);
      fetchAll();
    } catch (err) { alert('Error restoring document.'); }
  };

  const handleAcknowledge = async (docId, version) => {
    try {
      await axios.post(`${API}/documents/${docId}/acknowledge`, { user_id: user.id, document_version: version, ip_address: 'internal' });
      alert('Document acknowledged.');
      setSelected(null);
    } catch (err) { alert('Error acknowledging.'); }
  };

  const SortIcon = ({ col }) => {
    if (sortCol !== col) return <span className="sort-icon">⇅</span>;
    return <span className="sort-icon active">{sortDir === 'asc' ? '↑' : '↓'}</span>;
  };

  if (loading) return <div className="docs-loading">Loading documents...</div>;

  // ── ADD DOCUMENT FORM ────────────────────────────────────────
  if (adding && user.role === 'admin') return (
    <div className="doc-add-form">
      <button className="staff-back" onClick={() => { setAdding(false); setForm(EMPTY_FORM); setFile(null); }}>← DOCUMENTS</button>
      <div className="doc-add-header">
        <h2>ADD DOCUMENT</h2>
        <p>Drop a file and Claude will read it and fill in the details automatically</p>
      </div>
      <div
        className={`smart-drop-zone ${dragOver ? 'drag-over' : ''} ${file ? 'has-file' : ''}`}
        onDrop={handleDrop}
        onDragOver={e => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onClick={() => fileInputRef.current?.click()}
      >
        {analysing ? (
          <div className="drop-analysing"><div className="drop-spinner" /><span>{analysisStatus}</span></div>
        ) : file ? (
          <div className="drop-file-selected">
            <span className="drop-file-icon">📄</span>
            <div>
              <div className="drop-file-name">{file.name}</div>
              <div className="drop-file-size">{(file.size / 1024).toFixed(0)} KB — {analysisStatus || 'Fields filled below'}</div>
            </div>
            <button className="drop-change" onClick={e => { e.stopPropagation(); fileInputRef.current?.click(); }}>CHANGE</button>
          </div>
        ) : (
          <div className="drop-empty">
            <div className="drop-icon">⬆</div>
            <div className="drop-text">Drop your document here or click to browse</div>
            <div className="drop-hint">PDF, Word, or image — Claude will read and fill in the fields</div>
          </div>
        )}
        <input ref={fileInputRef} type="file" accept=".pdf,.doc,.docx,.png,.jpg,.jpeg" onChange={e => handleFileSelect(e.target.files[0])} style={{ display: 'none' }} />
      </div>

      {analysisStatus && !analysing && <div className="analysis-status">{analysisStatus}</div>}

      {detectedPerson && (
        <div className="person-detected">
          <span className="person-detected-icon">👤</span>
          <span>Document linked to <strong>{detectedPerson.first_name} {detectedPerson.last_name}</strong> — will be saved to their training records</span>
          <button className="person-detected-clear" onClick={() => setDetectedPerson(null)}>✕ unlink</button>
        </div>
      )}

      <div className="edit-form">
        <div className="edit-section-title">DOCUMENT DETAILS</div>
        <div className="edit-grid">
          <div className="edit-field full-width">
            <label>TITLE *</label>
            <input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="Document title" />
          </div>
          <div className="edit-field">
            <label>DOCUMENT REFERENCE (e.g. BE48)</label>
            <input value={form.doc_ref} onChange={e => setForm({ ...form, doc_ref: e.target.value })} placeholder="e.g. BE48" />
          </div>
          <div className="edit-field full-width">
            <label>DESCRIPTION</label>
            <input value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="Brief description" />
          </div>
          <div className="edit-field">
            <label>CATEGORY</label>
            <select value={form.category} onChange={e => setForm({ ...form, category: e.target.value })}>
              <option value="policy">Policy</option>
              <option value="procedure">Procedure</option>
              <option value="certificate">Certificate</option>
              <option value="risk assessment">Risk Assessment</option>
              <option value="record">Record</option>
              <option value="other">Other</option>
            </select>
          </div>
          <div className="edit-field">
            <label>ACHILLES SECTION</label>
            <select value={form.section} onChange={e => setForm({ ...form, section: e.target.value })}>
              <option value="">— Select section —</option>
              {Object.entries(SECTION_NAMES).map(([k, v]) => <option key={k} value={k}>{k}. {v}</option>)}
            </select>
          </div>
          <div className="edit-field">
            <label>VERSION</label>
            <input value={form.version} onChange={e => setForm({ ...form, version: e.target.value })} />
          </div>
          <div className="edit-field">
            <label>STATUS</label>
            <select value={form.status} onChange={e => setForm({ ...form, status: e.target.value })}>
              <option value="active">Active</option>
              <option value="draft">Draft</option>
            </select>
          </div>
          <div className="edit-field">
            <label>EXPIRY DATE</label>
            <input type="date" value={form.expiry_date} onChange={e => setForm({ ...form, expiry_date: e.target.value })} />
          </div>
          <div className="edit-field">
            <label>REVIEW DATE</label>
            <input type="date" value={form.review_date} onChange={e => setForm({ ...form, review_date: e.target.value })} />
          </div>
          <div className="edit-field">
            <label>ALERT (DAYS BEFORE EXPIRY)</label>
            <input type="number" value={form.reminder_days} onChange={e => setForm({ ...form, reminder_days: e.target.value })} />
          </div>
          <div className="edit-field">
            <label>REQUIRES STAFF ACKNOWLEDGEMENT</label>
            <select value={form.requires_acknowledgement} onChange={e => setForm({ ...form, requires_acknowledgement: e.target.value === 'true' })}>
              <option value="false">No</option>
              <option value="true">Yes</option>
            </select>
          </div>
          <div className="edit-field full-width">
            <label>EXTERNAL LINK (Margo, Motion, Cawleys etc.)</label>
            <input value={form.external_link} onChange={e => setForm({ ...form, external_link: e.target.value })} placeholder="https://..." />
          </div>
        </div>
        {uploadProgress && <div className="upload-progress">{uploadProgress}</div>}
        <div className="edit-actions">
          <button className="edit-btn secondary" onClick={() => { setAdding(false); setForm(EMPTY_FORM); setFile(null); }}>CANCEL</button>
          <button className={`edit-btn primary ${saving || analysing ? 'disabled' : ''}`} onClick={handleSubmit} disabled={saving || analysing}>
            {saving ? 'SAVING...' : 'SAVE DOCUMENT'}
          </button>
        </div>
      </div>
    </div>
  );

  // ── MAIN VIEW ────────────────────────────────────────────────
  return (
    <div className="documents">

      {/* Expiry banner */}
      {expiring.length > 0 && (
        <div className="expiry-banner">
          <span className="expiry-icon">⚠</span>
          <span><strong>{expiring.length} document{expiring.length > 1 ? 's' : ''}</strong> expiring soon or overdue</span>
          <button className="expiry-link" onClick={() => setStatusFilter('Expiring Soon')}>View all</button>
        </div>
      )}

      {/* Stat cards */}
      <div className="reg-summary">
        <div className="reg-stat" onClick={() => { setStatusFilter('All'); setShowArchived(false); }} style={{ cursor: 'pointer' }}>
          <span className="reg-stat-value">{counts.total}</span>
          <span className="reg-stat-label">Total Active</span>
        </div>
        <div className="reg-stat green" onClick={() => setStatusFilter('Current')} style={{ cursor: 'pointer' }}>
          <span className="reg-stat-value">{counts.current}</span>
          <span className="reg-stat-label">Current</span>
        </div>
        <div className="reg-stat amber" onClick={() => setStatusFilter('Expiring Soon')} style={{ cursor: 'pointer' }}>
          <span className="reg-stat-value">{counts.expiring}</span>
          <span className="reg-stat-label">Expiring Soon</span>
        </div>
        <div className="reg-stat red" onClick={() => setStatusFilter('Overdue')} style={{ cursor: 'pointer' }}>
          <span className="reg-stat-value">{counts.overdue}</span>
          <span className="reg-stat-label">Overdue</span>
        </div>
        <div className="reg-stat grey" onClick={() => setShowArchived(a => !a)} style={{ cursor: 'pointer' }}>
          <span className="reg-stat-value">{counts.archived}</span>
          <span className="reg-stat-label">Archived</span>
        </div>
      </div>

      {/* Controls bar */}
      <div className="docs-controls">
        <div className="docs-search">
          <span className="search-icon">🔍</span>
          <input type="text" placeholder="Search documents..." value={search} onChange={e => setSearch(e.target.value)} className="search-input" />
        </div>
        <div className="docs-header-actions">
          {/* View toggle */}
          <div className="view-toggle">
            <button className={`view-toggle-btn ${view === 'card' ? 'active' : ''}`} onClick={() => setView('card')} title="Card view">⊞</button>
            <button className={`view-toggle-btn ${view === 'table' ? 'active' : ''}`} onClick={() => setView('table')} title="Table view">☰</button>
          </div>
          {view === 'table' && (
            <button className="reg-export-btn" onClick={() => exportToCSV(filtered, expiringIds)}>↓ Export CSV</button>
          )}
          {user.role === 'admin' && <button className="doc-add-btn" onClick={() => setAdding(true)}>+ ADD DOCUMENT</button>}
        </div>
      </div>

      {/* Filters */}
      <div className="docs-filters">
        {CATEGORIES.map(c => (
          <button key={c} className={`filter-btn ${catFilter === c ? 'active' : ''}`} onClick={() => setCatFilter(c)}>
            {c === 'All' ? 'ALL' : c.toUpperCase()}
          </button>
        ))}
        <div className="filter-divider" />
        {STATUSES.map(s => (
          <button key={s} className={`filter-btn status-filter ${statusFilter === s ? 'active' : ''}`} onClick={() => setStatusFilter(s)}>
            {s.toUpperCase()}
            {s === 'Expiring Soon' && counts.expiring > 0 && <span className="filter-badge">{counts.expiring}</span>}
            {s === 'Overdue' && counts.overdue > 0 && <span className="filter-badge red">{counts.overdue}</span>}
          </button>
        ))}
      </div>

      <div className="docs-count">
        Showing {filtered.length} document{filtered.length !== 1 ? 's' : ''}
        {showArchived && <span className="archived-note"> (including archived)</span>}
      </div>

      {/* ── CARD VIEW ── */}
      {view === 'card' && (
        filtered.length === 0
          ? <div className="docs-empty"><p>No documents found.</p></div>
          : Object.keys(groupedBySection).sort((a, b) => Number(a) - Number(b)).map(section => (
            <div key={section} className="docs-section">
              <h3 className="section-title">
                {section > 0 ? `Section ${section} — ${SECTION_NAMES[section] || 'Other'}` : 'General'}
              </h3>
              <div className="docs-grid">
                {groupedBySection[section].map(doc => {
                  const status = getDocStatus(doc, expiringIds);
                  const days = doc.expiry_date ? daysUntilExpiry(doc.expiry_date) : null;
                  return (
                    <div key={doc.id} className={`doc-card ${status === 'Expiring Soon' || status === 'Overdue' ? 'expiring' : ''} ${doc.is_archived ? 'archived-doc' : ''}`} onClick={() => setSelected(doc)}>
                      <div className="doc-card-top">
                        <span className="doc-category" style={{ background: CATEGORY_COLOURS[doc.category] || '#607D8B' }}>{doc.category?.toUpperCase()}</span>
                        {doc.is_archived && <span className="archived-badge">ARCHIVED</span>}
                        {!doc.is_archived && (status === 'Expiring Soon' || status === 'Overdue') && (
                          <span className={`doc-expiry-badge ${status === 'Overdue' ? 'overdue' : days < 30 ? 'urgent' : ''}`}>
                            {status === 'Overdue' ? 'OVERDUE' : `${days}d`}
                          </span>
                        )}
                      </div>
                      <h4 className="doc-title">{doc.title}</h4>
                      {doc.description && <p className="doc-desc">{doc.description}</p>}
                      <div className="doc-meta">
                        {doc.doc_ref && <span className="doc-ref">{doc.doc_ref}</span>}
                        <span className="doc-version">v{doc.version}</span>
                        {doc.expiry_date && <span className="doc-expiry-date">Expires {new Date(doc.expiry_date).toLocaleDateString('en-GB')}</span>}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))
      )}

      {/* ── TABLE VIEW ── */}
      {view === 'table' && (
        <div className="reg-table-wrap">
          <table className="reg-table">
            <thead>
              <tr>
                {[
                  { key: 'doc_ref',     label: 'Ref' },
                  { key: 'title',       label: 'Title' },
                  { key: 'category',    label: 'Category' },
                  { key: 'section',     label: 'Section' },
                  { key: 'version',     label: 'Version' },
                  { key: 'created_at',  label: 'Issue Date' },
                  { key: 'expiry_date', label: 'Review / Expiry' },
                  { key: 'status',      label: 'Status' },
                ].map(col => (
                  <th key={col.key} onClick={() => handleSort(col.key)} className="sortable">
                    {col.label} <SortIcon col={col.key} />
                  </th>
                ))}
                <th>File</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr><td colSpan={9} className="reg-empty">No documents match your filters.</td></tr>
              )}
              {filtered.map(doc => {
                const status = getDocStatus(doc, expiringIds);
                return (
                  <tr key={doc.id} className={`reg-row ${doc.is_archived ? 'archived-row' : ''}`} onClick={() => setSelected(doc)} style={{ cursor: 'pointer' }}>
                    <td className="reg-ref">{doc.doc_ref || '—'}</td>
                    <td className="reg-doc-title">{doc.title}</td>
                    <td><span className="reg-cat">{capitalise(doc.category)}</span></td>
                    <td className="reg-section">{SECTION_FULL(doc.section)}</td>
                    <td className="reg-version">{doc.version || '—'}</td>
                    <td>{formatDate(doc.created_at)}</td>
                    <td>{formatDate(doc.expiry_date || doc.review_date)}</td>
                    <td><StatusBadge status={status} /></td>
                    <td onClick={e => e.stopPropagation()}>
                      {doc.file_url
                        ? <a className="reg-view-link" href={doc.file_url} target="_blank" rel="noreferrer">View ↗</a>
                        : '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <div className="reg-footer">
            Register generated live from Vera QMS · {new Date().toLocaleString('en-GB')}
          </div>
        </div>
      )}

      {/* ── DOCUMENT MODAL ── */}
      {selected && (
        <div className="modal-overlay" onClick={() => setSelected(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setSelected(null)}>✕</button>
            <div className="modal-header">
              <span className="doc-category" style={{ background: CATEGORY_COLOURS[selected.category] || '#607D8B' }}>{selected.category?.toUpperCase()}</span>
              <h2 className="modal-title">{selected.title}</h2>
              {selected.description && <p className="modal-desc">{selected.description}</p>}
            </div>
            <div className="modal-details">
              {selected.doc_ref && <div className="detail-row"><span className="detail-label">REFERENCE</span><span className="detail-value doc-ref">{selected.doc_ref}</span></div>}
              <div className="detail-row"><span className="detail-label">VERSION</span><span className="detail-value">{selected.version}</span></div>
              <div className="detail-row"><span className="detail-label">STATUS</span><StatusBadge status={getDocStatus(selected, expiringIds)} /></div>
              {selected.expiry_date && <div className="detail-row"><span className="detail-label">EXPIRES</span><span className={`detail-value ${expiringIds.has(selected.id) ? 'text-yellow' : ''}`}>{new Date(selected.expiry_date).toLocaleDateString('en-GB')}</span></div>}
              {selected.review_date && <div className="detail-row"><span className="detail-label">REVIEW DATE</span><span className="detail-value">{new Date(selected.review_date).toLocaleDateString('en-GB')}</span></div>}
              <div className="detail-row"><span className="detail-label">SECTION</span><span className="detail-value">{selected.section ? `${selected.section} — ${SECTION_NAMES[selected.section]}` : '—'}</span></div>
            </div>
            <div className="modal-actions">
              {selected.file_url && <a href={selected.file_url} target="_blank" rel="noreferrer" className="modal-btn primary">VIEW DOCUMENT</a>}
              {selected.external_link && <a href={selected.external_link} target="_blank" rel="noreferrer" className="modal-btn secondary">OPEN IN SYSTEM</a>}
              {selected.requires_acknowledgement && <button className="modal-btn acknowledge" onClick={() => handleAcknowledge(selected.id, selected.version)}>ACKNOWLEDGE DOCUMENT</button>}
              {user.role === 'admin' && !selected.is_archived && <button className="modal-btn danger" onClick={() => handleArchiveDoc(selected.id)}>ARCHIVE DOCUMENT</button>}
              {user.role === 'admin' && selected.is_archived && <button className="modal-btn restore" onClick={() => handleUnarchiveDoc(selected.id)}>RESTORE DOCUMENT</button>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
